import React, { useState } from 'react';
import { Cta } from './Cta';
import { ChevronDown } from 'lucide-react';
import { Reveal } from './Reveal';
import { PathThread } from './PathThread';
import { useScore } from './ScoreContext';
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200" data-id="element-133" data-mp="1maxf7jz3oe71">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" onClick={() => setIsOpen(!isOpen)} data-id="element-134" data-mp="ply5e0ivb4cq">
        
        <span className="text-lg font-serif text-navy group-hover:text-seaglass transition-colors" data-id="element-135" data-mp="tp466sn7o3bt">
          {question}
        </span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-paper-dark transition-colors ${isOpen ? 'bg-seaglass/10' : 'group-hover:bg-paper'}`} data-id="element-136" data-mp="13kxo6rjp8p0z">
          
          <ChevronDown className={`w-5 h-5 text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180 text-seaglass' : ''}`} data-id="element-137" data-mp="2f0e0lxoayutr" />
          
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'}`} data-id="element-138" data-mp="9xxi8igqvbst">
        
        <div className="text-text-muted leading-relaxed pr-8" data-id="element-139" data-mp="yk5ngxbq73v">{answer}</div>
      </div>
    </div>;
}
export function FinishFaqCta() {
  const faqs = [{
    q: 'Is this book marketing?',
    a: 'No. Book marketing gets more people to the book. Author Leverage builds the path for serious readers after the book creates trust.'
  }, {
    q: 'Is this a funnel?',
    a: 'Not in the usual sense. A funnel pushes people toward a predetermined sale. This helps readers take a useful next step based on where they are.'
  }, {
    q: 'What if my book has not launched yet?',
    a: 'This can be built before, during, or after launch. The page is written for authors whose book is already in the world because that is where the gap is easiest to feel. But pre-launch authors can still build the path before readers arrive.'
  }, {
    q: 'Will I know exactly who bought my book?',
    a: 'No. Amazon and most retailers do not give you reader data. This gives serious readers a useful reason to raise their hand, so you can see who wants to keep going.'
  }, {
    q: 'Do I need a huge audience?',
    a: 'No. If your book supports high-value work, a small number of the right readers taking a useful next step can matter.'
  }, {
    q: 'Do you guarantee revenue?',
    a: 'No. The build gives serious readers a clearer path and gives you a better way to see what they may need. Revenue depends on your offer, audience, follow-up, and fit.'
  }, {
    q: 'How much time does this take from me?',
    a: 'We do the heavy lifting. We need your judgment, your point of view, and a few focused working sessions to extract the core ideas, review the path, and approve the build.'
  }, {
    q: 'What happens after the diagnostic?',
    a: 'You get your Reader-to-Revenue Score. If there is a gap we can help fix, the next step is a Reader Path Audit.'
  }, {
    q: 'Do I have to manage the tech?',
    a: 'No. The build is designed to work behind your existing book, site, and offers without turning you into the tech manager.'
  }];
  const {
    answeredCount,
    openDiagnostic
  } = useScore();
  const ctaLabel = answeredCount === 0 ? 'Get Your Reader-to-Revenue Score' : answeredCount < 4 ? 'Continue Your Score' : 'Finish Your Score';
  const handleCtaClick = () => {
    openDiagnostic();
  };
  const stateAwareCopy = answeredCount === 0 ? 'Start with 8 questions about what happens after someone reads your book.' : answeredCount < 4 ? 'You have already started. Finish the remaining questions to get your Reader-to-Revenue Score.' : 'You are halfway there. Answer the remaining questions to get your Reader-to-Revenue Score.';
  return <section id="final-score-block" className="py-24 px-6 max-w-3xl mx-auto relative scroll-mt-24" data-id="element-140" data-mp="2n7dsh4i2x5uk">
      
      <div className="absolute -top-12 left-1/2 -translate-x-1/2" data-id="element-141" data-mp="2i9kpslfzitz1">
        <PathThread data-id="element-142" data-mp="rts4qd5wi14r" />
      </div>

      {/* Process Visual */}
      <div className="text-center mb-32" data-id="element-143" data-mp="wrtkop815kc0">
        <Reveal data-id="element-144" data-mp="wsmlzao7zhmr">
          <h2 className="text-3xl md:text-5xl font-serif text-navy mb-8 leading-tight" data-id="element-145" data-mp="ws3gitfralpm">
            Get the score before you <br data-id="element-146" data-mp="10vjbllc6ge07" />
            <span className="italic text-seaglass" data-id="element-147" data-mp="15si3ryca6avr">decide what to build</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} data-id="element-148" data-mp="1arumq8016xt2">
          <p className="text-lg text-text-muted mb-16" data-id="element-149" data-mp="1aqjnmlhj6e8a">{stateAwareCopy}</p>
        </Reveal>

        <Reveal delay={0.2} data-id="element-150" data-mp="1fpwah68ejlav">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8" data-id="element-151" data-mp="1fouv4pkcscnc">
            <div className="flex flex-col items-center gap-4" data-id="element-152" data-mp="1khxooxp4rm3j">
              <div className="w-14 h-14 rounded-full bg-seaglass text-white flex items-center justify-center font-serif text-xl shadow-[0_4px_12px_-4px_rgba(139,168,159,0.6)]" data-id="element-153" data-mp="1oj99mpx27j5e">
                1
              </div>
              <div className="text-sm font-medium text-navy text-center max-w-[120px]" data-id="element-154" data-mp="1yhyrwjsvb5m6">
                Get your Reader-to-Revenue Score
              </div>
            </div>
            <div className="hidden md:block w-16 h-px bg-seaglass border-t border-dashed border-seaglass" data-id="element-155" data-mp="23lhosydijonf"></div>
            <div className="flex flex-col items-center gap-4 opacity-60" data-id="element-156" data-mp="1ym68trkz0n6w">
              <div className="w-14 h-14 rounded-full bg-white border-2 border-gray-200 text-gray-400 flex items-center justify-center font-serif text-xl" data-id="element-157" data-mp="1ulmawlskpfjp">
                2
              </div>
              <div className="text-sm font-medium text-gray-500 text-center max-w-[120px]" data-id="element-158" data-mp="2mcippry71u9m">
                Audit the path
              </div>
            </div>
            <div className="hidden md:block w-16 h-px bg-gray-300 border-t border-dashed border-gray-300" data-id="element-159" data-mp="2hk8xg59lwi46"></div>
            <div className="flex flex-col items-center gap-4 opacity-60" data-id="element-160" data-mp="2di4afpdnyjqi">
              <div className="w-14 h-14 rounded-full bg-white border-2 border-gray-200 text-gray-400 flex items-center justify-center font-serif text-xl" data-id="element-161" data-mp="3a2we98hklazg">
                3
              </div>
              <div className="text-sm font-medium text-gray-500 text-center max-w-[120px]" data-id="element-162" data-mp="13f5l6p7d61dx">
                Decide whether to build
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* FAQ */}
      <div className="mb-32" data-id="element-163" data-mp="tlyjbebu13il">
        <Reveal data-id="element-164" data-mp="ymumtqs9unnh">
          <h3 className="text-3xl font-serif text-navy mb-10 text-center" data-id="element-165" data-mp="ymce3uofagyo">
            Frequently Asked Questions
          </h3>
        </Reveal>
        <Reveal delay={0.1} data-id="element-166" data-mp="kpfr64bfdjn2">
          <div className="border-t border-gray-200" data-id="element-167" data-mp="pm66gpfeob7f">
            {faqs.map((faq, idx) => <FaqItem key={idx} question={faq.q} answer={faq.a} data-id="element-168" data-mp="pk35xbgmmzog" data-mpi={idx} />)}
          </div>
        </Reveal>
      </div>

      {/* Final CTA */}
      <Reveal data-id="element-169" data-mp="fnqeylcguzxe">
        <div className="text-center bg-white rounded-[2.5rem] p-12 md:p-16 border border-gray-100 shadow-[0_20px_40px_-15px_rgba(26,40,56,0.1)] relative overflow-hidden" data-id="element-170" data-mp="fpb9iays6iv9">
          <div className="absolute top-0 left-0 right-0 h-2 bg-seaglass" data-id="element-171" data-mp="2a211uo9i3k0j"></div>
          <h2 className="text-3xl md:text-5xl font-serif text-navy mb-10 leading-tight" data-id="element-172" data-mp="29zx0acz2qx3f">
            You already did the hard part. <br data-id="element-173" data-mp="3plij7lp2n7hw" />
            <span className="italic text-seaglass" data-id="element-174" data-mp="3pm0dwdw35dnp">
              Now see what happens after someone reads it.
            </span>
          </h2>
          <div className="flex flex-col items-center gap-5" data-id="element-175" data-mp="86tvbrfymfvy">
            <Cta variant="primary" showArrow onClick={handleCtaClick} className="shadow-[0_8px_16px_-8px_rgba(212,165,116,0.5)]" data-id="element-176" data-mp="3czdktjx0azi">
              
              {ctaLabel}
            </Cta>
            <span className="text-sm text-text-muted font-medium" data-id="element-177" data-mp="d4lmukcozzzm">
              8 questions. No manuscript upload required.
            </span>
          </div>
        </div>
      </Reveal>
    </section>;
}